import {
  Component,
  Input,
  OnInit,
  OnDestroy,
  ViewChild,
  ElementRef,
  AfterViewInit,
  NgZone,
  Inject,
  PLATFORM_ID,
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { mapsConfig } from '../../../core/config/maps.config';

declare var google: any;

@Component({
  selector: 'app-safe-maps',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="maps-container">
      @if (!isBrowser || mapError) {
      <!-- Fallback pour SSR ou erreur -->
      <div class="fallback-map">
        <div class="fallback-content">
          <div class="fallback-icon">📍</div>
          <div class="fallback-text">
            <div class="coordinates">
              {{ latitude | number : '1.4-4' }},
              {{ longitude | number : '1.4-4' }}
            </div>
            <div class="zoom-level">Zoom: {{ zoom }}</div>
          </div>
        </div>
      </div>
      } @else {
      <!-- Carte Google Maps -->
      <div #mapElement class="map-element"></div>
      @if (loading) {
      <div class="loading-overlay">
        <div class="loading-spinner"></div>
      </div>
      } }
    </div>
  `,
  styles: [
    `
      .maps-container {
        width: 100%;
        height: 100%;
        position: relative;
      }

      .map-element {
        width: 100%;
        height: 100%;
        border-radius: 8px;
        border: 1px solid #e5e7eb;
      }

      .loading-overlay {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(255, 255, 255, 0.8);
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 8px;
      }

      .loading-spinner {
        width: 20px;
        height: 20px;
        border: 2px solid #f3f3f3;
        border-top: 2px solid #3b82f6;
        border-radius: 50%;
        animation: spin 1s linear infinite;
      }

      @keyframes spin {
        0% {
          transform: rotate(0deg);
        }
        100% {
          transform: rotate(360deg);
        }
      }

      .fallback-map {
        width: 100%;
        height: 100%;
        border-radius: 8px;
        border: 1px solid #e5e7eb;
        background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .fallback-content {
        text-align: center;
        color: #64748b;
      }

      .fallback-icon {
        font-size: 2rem;
        margin-bottom: 0.5rem;
      }

      .fallback-text {
        font-size: 0.875rem;
      }

      .coordinates {
        font-weight: 600;
        color: #475569;
        margin-bottom: 0.25rem;
      }

      .zoom-level {
        font-size: 0.75rem;
        opacity: 0.7;
      }
    `,
  ],
})
export class SafeMapsComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() latitude?: number = 0;
  @Input() longitude?: number = 0;
  @Input() zoom: number = 15;
  @Input() height: string = '100%';
  @Input() apiKey: string = mapsConfig.googleMapsApiKey;
  @Input() showMarker: boolean = true;
  @Input() draggable: boolean = false;
  @Input() clickableIcons: boolean = false;

  @ViewChild('mapElement', { static: false }) mapElement!: ElementRef;

  private map: any;
  private marker: any;
  private googleMapsLoaded = false;
  loading = true;
  mapError = false;
  isBrowser = false;

  constructor(
    private ngZone: NgZone,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  ngOnInit() {
    // En SSR, afficher directement le fallback
    if (!this.isBrowser) {
      this.mapError = true;
      this.loading = false;
      return;
    }

    // En browser, charger Google Maps de manière différée
    setTimeout(() => {
      this.loadGoogleMaps();
    }, 100);
  }

  ngAfterViewInit() {
    if (this.isBrowser && this.googleMapsLoaded) {
      this.initMap();
    }
  }

  ngOnDestroy() {
    if (this.isBrowser && this.map) {
      google.maps.event.clearInstanceListeners(this.map);
    }
  }

  private loadGoogleMaps() {
    if (!this.isBrowser) {
      return;
    }

    if (!this.apiKey) {
      this.ngZone.run(() => {
        console.warn(
          'Google Maps API key not provided. Using fallback display.'
        );
        this.mapError = true;
        this.loading = false;
      });
      return;
    }

    // Check if Google Maps is already loaded
    if (typeof google !== 'undefined' && google.maps) {
      this.googleMapsLoaded = true;
      setTimeout(() => this.initMap(), 100);
      return;
    }

    // Check if script is already present
    if (
      document.querySelector(
        'script[src^="https://maps.googleapis.com/maps/api/js"]'
      )
    ) {
      // Wait for script to load
      const interval = setInterval(() => {
        if (typeof google !== 'undefined' && google.maps) {
          clearInterval(interval);
          this.ngZone.run(() => {
            this.googleMapsLoaded = true;
            setTimeout(() => this.initMap(), 100);
          });
        }
      }, 100);
      return;
    }

    // Load Google Maps script dynamically
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${this.apiKey}&libraries=geometry,places&loading=async`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      this.ngZone.run(() => {
        this.googleMapsLoaded = true;
        setTimeout(() => this.initMap(), 100);
      });
    };
    script.onerror = (error) => {
      this.ngZone.run(() => {
        console.error('Failed to load Google Maps API:', error);
        this.mapError = true;
        this.loading = false;
      });
    };
    document.head.appendChild(script);
  }

  private initMap() {
    if (!this.isBrowser || !this.mapElement || !this.mapElement.nativeElement) {
      return;
    }

    try {
      const position = { lat: this.latitude, lng: this.longitude };

      // Map options
      const mapOptions = {
        center: position,
        zoom: this.zoom,
        mapTypeId: google.maps.MapTypeId.ROADMAP,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        zoomControl: false,
        scrollwheel: false,
        disableDoubleClickZoom: true,
        draggable: this.draggable,
        clickableIcons: this.clickableIcons,
        styles: [
          {
            featureType: 'poi',
            elementType: 'labels',
            stylers: [{ visibility: 'off' }],
          },
          {
            featureType: 'transit',
            elementType: 'labels',
            stylers: [{ visibility: 'off' }],
          },
        ],
      };

      // Create map
      this.map = new google.maps.Map(this.mapElement.nativeElement, mapOptions);

      // Add marker if requested
      if (this.showMarker) {
        this.addMarker(position);
      }

      // Hide loading
      this.loading = false;

      // Trigger resize to ensure proper rendering
      setTimeout(() => {
        google.maps.event.trigger(this.map, 'resize');
      }, 100);
    } catch (error) {
      this.ngZone.run(() => {
        console.error('Error initializing Google Maps:', error);
        this.mapError = true;
        this.loading = false;
      });
    }
  }

  private addMarker(position: any) {
    // AdvancedMarkerElement (new API)
    if (google.maps.marker && google.maps.marker.AdvancedMarkerElement) {
      const marker = new google.maps.marker.AdvancedMarkerElement({
        map: this.map,
        position: position,
        title: 'Location',
        content: document.createElement('div'), // You can customize marker content here
      });
      // Add click event
      marker.addListener('click', () => {
        this.ngZone.run(() => {
          console.log('AdvancedMarker clicked at:', position);
        });
      });
      this.marker = marker;
    } else {
      // Fallback to old Marker if AdvancedMarkerElement is not available
      const markerIcon = {
        url:
          'data:image/svg+xml;charset=UTF-8,' +
          encodeURIComponent(`
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="12" cy="12" r="8" fill="#3B82F6" stroke="white" stroke-width="2"/>
          <circle cx="12" cy="12" r="3" fill="white"/>
        </svg>
      `),
        scaledSize: new google.maps.Size(24, 24),
        anchor: new google.maps.Point(12, 12),
      };

      this.marker = new google.maps.Marker({
        position: position,
        map: this.map,
        title: 'Location',
        animation: google.maps.Animation.DROP,
        icon: markerIcon,
      });

      this.marker.addListener('click', () => {
        this.ngZone.run(() => {
          console.log('Marker clicked at:', position);
        });
      });
    }
  }

  // Public methods for external control
  public setCenter(lat: number, lng: number) {
    if (this.isBrowser && this.map) {
      const position = { lat, lng };
      this.map.setCenter(position);
      if (this.marker) {
        this.marker.setPosition(position);
      }
    }
  }

  public setZoom(zoom: number) {
    if (this.isBrowser && this.map) {
      this.map.setZoom(zoom);
    }
  }

  public fitBounds(bounds: any) {
    if (this.isBrowser && this.map) {
      this.map.fitBounds(bounds);
    }
  }
}
